import React, { useRef, useEffect, useState, Fragment} from 'react'; 
//import observer from 'mobx-react'; 
import { observer } from 'mobx-react';

export const PopupContainer = (props: {title?: String} ) => {
    return (
        <div>
            {props.title === 'Define AOI and DEM' && <AOIDEMDetails />} 
            {props.title === 'Define Outlet' && <OutletDetails />} 
            {props.title === 'Edit Watershed' && <WatershedDetails />} 
            {props.title === 'Land Use Land Cover' && <LULCDetails />} 
            {props.title === 'Hydrologic Soil Group (HSG)' && <HSGDetails />}
            {props.title === 'Dimensionless Hydrograph Unit (DUH)' && <DUHDetails />} 
            {props.title === 'Smooth Rainfall Distribution' && <SRDDetails />} 
            {props.title === 'Notes' && <NotesDetails />}
            {props.title === 'Adjusted Curve Number' && <AdjuCurveDetails />}
        </div>
    ); 
}; 

export const WatershedAnalysisDetails = observer(PopupContainer); 

export const AOIDEMDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                <strong>The Area of Interest (AOI)</strong> is a user defined extent used to clip data 
                from a USDA FPAC Geospatial Branch <strong>Digital Elevation Model service (DEM)</strong>. 
                The AOI may be digitized using one of the polygon, rectangle, or circle tools. 
                The larger the AOI and the higher resolution of the DEM service (i.e. 1m vs. 3m) can result in a clip with too much data. 
                In this case, reduce the AOI extent, or choose a lower resolution DEM. 
                Initially using a large AOI and a lower resolution DEM can be a technique to determine 
                the approximate watershed boundary to refine by going back to tighten the AOI and selecting 
                a higher resolution DEM for more accurate results. 
            </p>
        </div>
    ); 
}; 

export const OutletDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The <strong>Outlet</strong> for NETS Hydrology Analysis is defined by using the drawing tool 
                to create a line perpendicular to the flow path. 
                Only One Outlet can be used. The Hydraulic Connections tool allows the user to hydraulically connect 
                a portion of the watershed that may appear to be distinct in the DEM representation of 
                the site. A likely scenario is a culvert under a road fill.
            </p>
        </div>
    ); 
}; 

export const WatershedDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                To <strong>Edit the Watershed,</strong> click on the watershed polygon to highlight the exterior vertices as orange circles. 

                The circles can be deleted individually by right-clicking on the desired vertex, or the vertex can be moved 
                by dragging and dropping with the mouse.
            </p>
            <p>
                If the interior of the polygon is shaded red, portions of the watershed extend past the defined AOI. 
                The watershed polygon can be edited to remain inside the defined AOI, but the extent of the watershed 
                outside the AOI is unknown. The recommended correction is to use the Back button to return to the Define AOI step 
                and redefine the AOI so it fully contains the watershed. 
            </p>
            <p> 
                It is recommended to document the reason for editing the generated watershed polygon by adding a comment. 
            </p>
        </div>
    ); 
}; 

export const LULCDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                NETS Hydrology Analysis utilizes the NRCS Geospatial Data Gateway <strong> Land Use Land Cover (LULC)</strong> Cropland Data 
                layer for the 
                source of the initial polygons. 
                In many cases, the resolution of the data is too low to match the existing situation well. 
                The Polygons can be displayed individually by checking the box for the specific row, 
                in combination by selecting multiple rows, or all by selecting the box in the header row. 
            </p>
            <p> 
                The Split button will cut a polygon into two along a digitized polyline. 
                To split a polygon, the digitized line must initiate outside of the polygon and end outside of the polygon. 
                The polyline does not have to be straight and can consist of multiple segments. 
            </p>
            <p> 
                The Merge button will merge multiple selected polygons into a single polygon. 
            </p>
            <p> 
                A potential procedure to refine the polygons is to select background imagery (such as the USDA NAIP) 
                so the land use is visible along with the initially generated polygons. 
                If the initial polygons noticeably need significant edits, all can be selected and merged. 
                Then split the single, large polygon using the background imagery to assist digitizing the splitting polylines.
            </p>
        </div>
    ); 
}; 

export const HSGDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                A <strong>Hydrologic Soil Group (HSG)</strong> is a group of soils having similar physical and runoff characteristics. 
                Groupings range from group A (high infiltration/low runoff potential) to group D (slow infiltration/high runoff potential). 
            </p>
            <p>
                The View Details informational icon displays the percentages by group 
                for map unit components of a soil polygon. If there is a predominance of a HSG, the selection defaults, but can be changed if necessary.
            </p>
            <p>
                Some soils may be assigned to a dual hydrologic group (A/D, B/D, or C/D) where 
                the first letter is for drained areas and second is for undrained areas. 
                There is no rule to assign drained/undrained condition by default in NETS. 
                If there are components that are dual HSG, a checkmark is displayed in the Dual HSG column. 
                If the dual HSG is the predominant portion, it defaults to a red box and 
                a manual choice (based on knowledge of the site) must be made.
            </p>
        </div>
    ); 
}; 

export const DUHDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The <strong> Dimensionless Unit Hydrograph (DUH)</strong> used as an NRCS standard for 
                many years has a peak rate factor of 484. 

                The NETS default is Standard-484. The shape of the DUH determines the peak rate factor.

                The higher the peak rate factor, the higher the peak discharge will be (NEH Part 630 Hydrology, Chapter 16). 

                It can be changed if necessary (consult the State Conservation Engineer or 
                the State Hydraulic Engineer for applicably of a specific DUH). 

                The results must be recalculated if the DUH is changed by selecting the Submit button 
                at the lower left corner of the page. 
            </p>
        </div>
    ); 
}; 

export const SRDDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                <strong>Smooth Rainfall Distribution</strong> is a technique for smoothing 
                NOAA Atlas 14 precipitation data in the Win-TR20 hydrologic model. 

                If data are not smoothed, irregularly shaped hydrographs may 
                cause unexpected results (NEH Part 630 Hydrology, Chapter 4). 
                The NETS default is Yes. 

                If it is necessary to choose No (consult the State Conservation Engineer 
                or the State Hydraulic Engineer for applicably of not smoothing), the results 
                must be recalculated by selecting the Submit button at the lower left corner of the page. 
            </p>
        </div>
    ); 
}; 

export const NotesDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                It is recommended to document the reason for changing the DUH or smoothing by adding a comment.
            </p>
        </div>
    ); 
}; 

export const AdjuCurveDetails = (props: {className?: string}) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The <strong>Adjusted Curve Number</strong> input allows the user to directly modify 
                the system calculated Weighted Curve Number. Populating the 
                Adjusted Curve Number Field prompts an Alert to document the reasoning for the adjustment. 
                <br /><br />
                Similar to the Dimensionless Unit Hydrograph and Smooth Rainfall Distribution options, the Adjusted Curve Number 
                will require the user to Submit for recalculation and results are not available until that occurs.
                <br /><br />
                Adjustments over 15 will generate a Warning to Consult with your 
                State Conservation Engineer or State Hydraulic Engineer for applicability.
            </p>
        </div>
    ); 
}; 